import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import DOMPurify from "dompurify";
import { FaExternalLinkAlt, FaHandshake } from "react-icons/fa";

import { getAllPartenaires } from "../services/partenairesServices";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3317";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

const isExternalURL = (url) => {
  if (!url) return false;

  try {
    const newUrl = new URL(url);
    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch {
    return false;
  }
};

const normalizeUrl = (url) => {
  if (!url) return null;
  if (isExternalURL(url)) return url;
  if (url.startsWith("//")) return `https:${url}`;

  return `https://${url.replace(/^\/*/, "")}`;
};

const resolveImageUrl = (photoUrl) => {
  if (!photoUrl) return null;
  if (isExternalURL(photoUrl)) return photoUrl;

  const cleanPath = photoUrl.startsWith("/") ? photoUrl : `/${photoUrl}`;
  return `${BASE_URL}${cleanPath}`;
};

function PartnerCard({ partenaire, mobile = false }) {
  const href = normalizeUrl(partenaire?.link_url);
  const imageUrl = resolveImageUrl(partenaire?.photo_url);

  const cardContent = (
    <article
      className={cn(
        "group flex w-full min-w-0 flex-col overflow-hidden rounded-[28px]",
        "border border-slate-200/80 bg-white/90 shadow-lg shadow-slate-900/5",
        "transition duration-300 hover:-translate-y-1 hover:shadow-xl",
        "dark:border-white/10 dark:bg-slate-900/85 dark:shadow-black/20",
        mobile ? "p-4" : "h-full min-h-[360px] p-5"
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-[22px] bg-slate-50 p-4 dark:bg-slate-800/80",
          mobile ? "h-32" : "h-36"
        )}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={partenaire.nom}
            className="max-h-full w-full max-w-full object-contain transition duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-slate-400">
            <FaHandshake className="text-4xl" />
            <span className="text-sm">Logo indisponible</span>
          </div>
        )}
      </div>

      <div className="mt-5 flex flex-1 flex-col items-center text-center">
        <h3 className="text-lg font-bold text-slate-900 dark:text-white sm:text-xl">
          {partenaire.nom}
        </h3>

        <div
          className="mt-3 max-w-full break-words text-sm leading-relaxed text-slate-600 dark:text-slate-300"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: mobile ? 5 : 4,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(
              partenaire.description || "Description non disponible"
            ),
          }}
        />

        {href && (
          <div className="mt-auto pt-5">
            <span className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-xs font-bold uppercase tracking-wide text-white transition group-hover:bg-slate-700 dark:bg-white dark:text-slate-950">
              Voir le partenaire
              <FaExternalLinkAlt className="text-[10px]" />
            </span>
          </div>
        )}
      </div>
    </article>
  );

  if (!href) {
    return <div className="h-full w-full">{cardContent}</div>;
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      title={`Ouvrir ${partenaire.nom} dans un nouvel onglet`}
      className="block h-full w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-slate-300 dark:focus-visible:ring-white/20"
    >
      {cardContent}
    </a>
  );
}

const PartenairesCarousel = () => {
  const [partenaires, setPartenaires] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPartenaires = async () => {
      try {
        const allPartenaires = await getAllPartenaires();

        const activePartenaires = Array.isArray(allPartenaires)
          ? allPartenaires.filter((p) => p?.is_active)
          : [];

        setPartenaires(activePartenaires);
      } catch (error) {
        console.error("Erreur lors de la récupération des partenaires", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPartenaires();
  }, []);

  const sliderSettings = useMemo(() => {
    const count = partenaires.length;
    const hasSeveralPartners = count > 1;

    return {
      infinite: hasSeveralPartners,
      slidesToShow: Math.min(3, Math.max(count, 1)),
      slidesToScroll: 1,

      autoplay: hasSeveralPartners,
      autoplaySpeed: 4500,
      speed: 850,
      cssEase: "ease-in-out",

      arrows: count > 3,
      dots: count > 1,
      pauseOnHover: true,
      pauseOnFocus: true,
      swipeToSlide: true,
      adaptiveHeight: false,

      responsive: [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: Math.min(2, Math.max(count, 1)),
            arrows: false,
            dots: count > 1,
          },
        },
      ],
    };
  }, [partenaires.length]);

  if (loading) {
    return (
      <section className="py-10 dark:text-gray-200">
        <div className="container mx-auto px-4">
          <div className="mx-auto h-8 w-56 animate-pulse rounded-full bg-slate-200 dark:bg-slate-700" />

          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="h-[330px] animate-pulse rounded-[28px] bg-slate-200 dark:bg-slate-800"
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!partenaires.length) {
    return null;
  }

  return (
    <section className="relative overflow-hidden py-10 dark:text-gray-200 sm:py-14">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-slate-100/70 to-transparent dark:from-slate-950/40" />

      <div className="container relative mx-auto max-w-7xl px-4">
        <div className="mx-auto mb-8 max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 shadow-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <FaHandshake />
            Réseau local
          </span>

          <h2 className="mt-4 text-3xl font-bold text-slate-950 dark:text-white md:text-4xl">
            Mes partenaires
          </h2>

          <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300 sm:text-base">
            Découvrez les partenaires qui accompagnent l’activité et soutiennent
            le développement du shiatsu.
          </p>
        </div>

        {/* MOBILE : aucune utilisation de react-slick */}
        <div className="mx-auto grid w-full max-w-sm grid-cols-1 gap-5 md:hidden">
          {partenaires.map((partenaire) => (
            <PartnerCard
              key={partenaire.id}
              partenaire={partenaire}
              mobile
            />
          ))}
        </div>

        {/* DESKTOP / TABLETTE : on garde le carousel */}
        <div className="partners-slider hidden md:block">
          <Slider {...sliderSettings}>
            {partenaires.map((partenaire) => (
              <div key={partenaire.id} className="h-full px-3 pb-8">
                <PartnerCard partenaire={partenaire} />
              </div>
            ))}
          </Slider>
        </div>
      </div>

      <style>
        {`
          .partners-slider,
          .partners-slider .slick-slider,
          .partners-slider .slick-list {
            width: 100%;
            max-width: 100%;
            overflow: hidden;
          }

          .partners-slider .slick-track {
            display: flex;
            align-items: stretch;
          }

          .partners-slider .slick-slide {
            height: auto;
          }

          .partners-slider .slick-slide > div {
            height: 100%;
          }

          .partners-slider .slick-dots {
            bottom: -10px;
          }

          .partners-slider .slick-dots li button:before {
            font-size: 9px;
          }
        `}
      </style>
    </section>
  );
};

export default PartenairesCarousel;